import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static values = {
    url: String,
    fieldId: Number
  }

  connect() {
    this.isDeleting = false;
  }

  async confirmDelete(event) {
    event.preventDefault();

    // Prevent double-clicks
    if (this.isDeleting) return;

    // Show confirmation dialog
    const confirmed = confirm('Are you sure you want to delete this file? This action cannot be undone.');
    if (!confirmed) return;

    this.isDeleting = true;
    this.showDeletingState();

    try {
      await this.performDelete();
    } catch (error) {
      console.error('File deletion error:', error);
      this.showDeleteErrorState();
    } finally {
      this.isDeleting = false;
    }
  }

  async performDelete() {
    const formData = new FormData();
    formData.append('field_id', this.fieldIdValue);
    formData.append('field_value', ''); // Empty value to clear the field
    formData.append('authenticity_token', this.getAuthenticityToken());

    const response = await fetch(this.urlValue, {
      method: 'PATCH',
      body: formData,
      headers: {
        'Accept': 'text/vnd.turbo-stream.html',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (response.ok) {
      this.showDeletedState();
      // Hide the current file section and show the file input
      this.hideCurrentFileSection();
      this.showFileInput();
    } else {
      this.showDeleteErrorState();
    }
  }

  showDeletingState() {
    const deleteButton = this.element;
    deleteButton.disabled = true;
    deleteButton.innerHTML = '<i class="fas fa-spinner fa-spin me-1"></i>Deleting...';
  }

  showDeletedState() {
    // The turbo stream should handle the UI updates
    // But we can add a temporary success message
    this.addDeleteIndicator('✅', 'text-success', 'Deleted');
    this.hideDeleteIndicatorAfterDelay();
  }

  showDeleteErrorState() {
    const deleteButton = this.element;
    deleteButton.disabled = false;
    deleteButton.innerHTML = '<i class="fas fa-trash me-1"></i>Delete';
    this.addDeleteIndicator('❌', 'text-danger', 'Delete failed');
    this.hideDeleteIndicatorAfterDelay();
  }

  hideCurrentFileSection() {
    // The turbo stream response will handle hiding the current file section
    // This method is kept for compatibility but may not be needed
  }

  showFileInput() {
    // The turbo stream response will handle showing the file input
    // This method is kept for compatibility but may not be needed
  }

  addDeleteIndicator(icon, cssClass, title) {
    // Remove any existing indicators
    this.removeDeleteIndicators();

    const indicator = document.createElement('span');
    indicator.className = `delete-indicator ${cssClass} ms-2`;
    indicator.innerHTML = `<small>${icon}</small>`;
    indicator.title = title;

    const fieldContainer = this.element.closest('.form-field');
    if (fieldContainer) {
      const label = fieldContainer.querySelector('.field-label');
      if (label) {
        label.appendChild(indicator);
      }
    }
  }

  removeDeleteIndicators() {
    const indicators = document.querySelectorAll('.delete-indicator');
    indicators.forEach(indicator => indicator.remove());
  }

  hideDeleteIndicatorAfterDelay() {
    setTimeout(() => {
      this.removeDeleteIndicators();
    }, 3000); // Remove indicator after 3 seconds
  }

  getAuthenticityToken() {
    const tokenElement = document.querySelector('meta[name="csrf-token"]');
    return tokenElement ? tokenElement.content : '';
  }
}